import { supabase } from '../client'
import type { CloudTag } from '../types'
import { logSupabaseError } from './logSupabaseError'

/* ── TAGS (Cloud) ─────────────────────────────────────────── */

/**
 * Get all tags for user
 */
export async function getCloudTags(userId: string, since?: Date): Promise<CloudTag[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('tags')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (since) {
      query = query.gte('updated_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      logSupabaseError('Get tags error', error)
      return []
    }

    return data || []
  } catch (error) {
    logSupabaseError('Get tags error', error)
    return []
  }
}

/**
 * Get all cloud tag IDs for a user (lightweight — only fetches id column).
 * Used for ID-set reconciliation to detect tags missing from local DB.
 */
export async function getCloudTagIds(userId: string): Promise<string[]> {
  if (!supabase) return []

  try {
    const { data, error } = await supabase
      .from('tags')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(10000)

    if (error) {
      logSupabaseError('Get tag IDs error', error)
      return []
    }

    return (data || []).map(row => row.id)
  } catch (error) {
    logSupabaseError('Get tag IDs error', error)
    return []
  }
}

/**
 * Get specific cloud tags by their IDs.
 * Used for targeted fetch of tags missing from local DB.
 */
export async function getCloudTagsByIds(userId: string, ids: string[]): Promise<CloudTag[]> {
  if (!supabase || ids.length === 0) return []

  try {
    const { data, error } = await supabase
      .from('tags')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)

    if (error) {
      logSupabaseError('Get tags by IDs error', error)
      return []
    }

    return data || []
  } catch (error) {
    logSupabaseError('Get tags by IDs error', error)
    return []
  }
}

/**
 * Get cloud tags that were deleted since a given date
 */
export async function getDeletedCloudTags(userId: string, since?: Date): Promise<{ id: string; local_id: string | null; updated_at: string }[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('tags')
      .select('id, local_id, updated_at')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)

    if (since) {
      query = query.gte('deleted_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      logSupabaseError('Get deleted tags error', error)
      return []
    }

    return data || []
  } catch (error) {
    logSupabaseError('Get deleted tags error', error)
    return []
  }
}

/**
 * Create a new tag
 */
export async function createCloudTag(
  userId: string,
  tag: Omit<CloudTag, 'id' | 'user_id' | 'created_at' | 'updated_at'>
): Promise<CloudTag | null> {
  if (!supabase) return null

  try {
    const { data, error } = await supabase
      .from('tags')
      .insert({
        ...tag,
        user_id: userId,
      })
      .select()
      .single()

    if (error) {
      logSupabaseError('Create tag error', error)
      return null
    }

    return data
  } catch (error) {
    logSupabaseError('Create tag error', error)
    return null
  }
}

/**
 * Update a tag. Returns the server-assigned updated_at timestamp on success,
 * or null on failure.
 */
export async function updateCloudTag(
  tagId: string,
  updates: Partial<Omit<CloudTag, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null

  try {
    let query = supabase
      .from('tags')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', tagId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query
      .select('updated_at')
      .single()

    if (error) {
      logSupabaseError('Update tag error', error)
      return null
    }

    return data
  } catch (error) {
    logSupabaseError('Update tag error', error)
    return null
  }
}

/**
 * Delete a tag (soft delete)
 */
export async function deleteCloudTag(tagId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false

  try {
    let query = supabase
      .from('tags')
      .update({
        deleted_at: new Date().toISOString(),
      })
      .eq('id', tagId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query

    if (error) {
      logSupabaseError('Delete tag error', error)
      return false
    }

    return true
  } catch (error) {
    logSupabaseError('Delete tag error', error)
    return false
  }
}
